import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, PlayCircle, ClipboardList, CheckCircle2, PenTool,
  Clock, Radio, ChevronRight, AlertTriangle,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";

const POLL_MS = 15000;

/**
 * SwingProgressTracker
 *  - Live tracker for each active swing.
 *  - One card per swing; one row per item (machine + template).
 *  - Row shows a progress bar based on the linked inspection's status.
 *  - Polls every 15s while at least one active swing exists.
 */
export default function SwingProgressTracker({ testid = "swing-progress" }) {
  const [swings, setSwings] = useState(null);          // full list
  const [details, setDetails] = useState({});           // sid -> detail (items)
  const [inspections, setInspections] = useState([]);   // for progress lookups
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  const [selectedInsp, setSelectedInsp] = useState(null);
  const pollRef = useRef(null);

  const load = async (opts = { showBusy: true }) => {
    if (opts.showBusy) setBusy(true);
    setErr("");
    try {
      const [swingsRes, inspRes] = await Promise.allSettled([
        api.get("/swings"),
        api.get("/inspections"),
      ]);
      const swingsArr = extract(swingsRes);
      const inspArr = extract(inspRes);
      setSwings(swingsArr);
      setInspections(inspArr);

      // Hydrate details for active swings
      const active = swingsArr.filter(isActiveSwing);
      const missing = active.filter((s) => !details[s.id || s._id]);
      if (missing.length) {
        const detailResults = await Promise.allSettled(
          missing.map((s) => api.get(`/swings/${s.id || s._id}`))
        );
        setDetails((prev) => {
          const next = { ...prev };
          missing.forEach((s, i) => {
            const r = detailResults[i];
            if (r.status === "fulfilled") next[s.id || s._id] = r.value.data;
          });
          return next;
        });
      }
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load({ showBusy: true });
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSwings = useMemo(() => (swings || []).filter(isActiveSwing), [swings]);

  // Start / stop poll when active count changes
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (activeSwings.length > 0) {
      pollRef.current = setInterval(() => load({ showBusy: false }).catch(() => {}), POLL_MS);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSwings.length]);

  const inspectionsById = useMemo(() => {
    const m = new Map();
    (inspections || []).forEach((i) => m.set(String(i.id || i._id), i));
    return m;
  }, [inspections]);

  return (
    <section className="panel-heavy p-5 md:p-6" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center relative"
             style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
          <Radio className="w-5 h-5" />
          {activeSwings.length > 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse"
                  style={{ background: "var(--primary)", boxShadow: "0 0 8px var(--primary)" }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted">Live · updates every 15s</div>
          <div className="font-display font-black text-xl tracking-tight">
            Swing Progress Tracker
            {activeSwings.length > 0 && (
              <span className="ml-2 text-sm text-muted font-mono-tight">· {activeSwings.length} active</span>
            )}
          </div>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={() => load({ showBusy: true })} disabled={busy} data-testid="swing-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start mb-3">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {busy && !swings ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading swings…
        </div>
      ) : activeSwings.length === 0 ? (
        <div className="panel-flat p-8 text-center" data-testid="swing-empty">
          <PlayCircle className="w-8 h-8 mx-auto mb-2 text-muted" />
          <div className="font-display font-black text-lg">No active swings</div>
          <div className="text-muted text-sm mt-1">
            Start a swing above and it will appear here with live progress.
          </div>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="swing-cards">
          {activeSwings.map((sw) => (
            <SwingCard
              key={sw.id || sw._id}
              swing={sw}
              detail={details[sw.id || sw._id]}
              inspectionsById={inspectionsById}
              onOpenInspection={(ins) => setSelectedInsp(ins)}
            />
          ))}
        </div>
      )}

      {selectedInsp && (
        <DetailDrawer
          item={selectedInsp}
          onClose={() => setSelectedInsp(null)}
          onUpdated={() => { setSelectedInsp(null); load({ showBusy: false }); }}
          itemLabel={(it) => it.template_name || it.title || `Inspection #${String(it.id || "").slice(0, 6)}`}
          resourceName="Inspection"
        />
      )}
    </section>
  );
}

/* ---------------- Swing card ---------------- */
function SwingCard({ swing, detail, inspectionsById, onOpenInspection }) {
  const items = itemsFrom(swing, detail);
  const progress = items.map((it) => progressFor(it, inspectionsById));
  const overall = progress.length
    ? Math.round(progress.reduce((a, b) => a + b.pct, 0) / progress.length)
    : 0;
  const label = swing.swing_label || swing.label || `Swing · ${fmtDate(swing.start_date || swing.created_at)}`;
  const dateRange = fmtRange(swing.start_date, swing.end_date);
  const workArea = swing.work_area || swing.site_code || "";

  return (
    <div className="panel p-4 md:p-5" data-testid={`swing-card-${swing.id || swing._id}`}>
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg tracking-tight truncate">{label}</div>
          <div className="text-[11px] font-mono-tight text-muted mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
            {dateRange && <span><Clock className="w-3 h-3 inline mr-1" />{dateRange}</span>}
            {workArea && <span>· {workArea}</span>}
            <span>· {items.length} inspection{items.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-3xl tracking-tighter tabular-nums"
               style={{ color: overall >= 100 ? "#4ade80" : "var(--primary)" }}>
            {overall}<span className="text-lg text-muted">%</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted">Overall</div>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="h-2 rounded-full overflow-hidden mb-4"
           style={{ background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)" }}>
        <div className="h-full transition-all duration-500 ease-out"
             style={{
               width: `${overall}%`,
               background: overall >= 100
                 ? "linear-gradient(90deg,#1e6b32,#4ade80)"
                 : "linear-gradient(90deg,#f58220,#ffb347)",
             }} />
      </div>

      {/* Per-inspection rows */}
      {items.length === 0 ? (
        <div className="text-muted text-sm py-2">No items on this swing yet.</div>
      ) : (
        <div className="grid gap-2">
          {items.map((it, idx) => {
            const p = progress[idx];
            const insp = it._insp;
            return (
              <button
                key={it.id || it._id || idx}
                type="button"
                onClick={() => insp && onOpenInspection(insp)}
                className="panel-flat p-3 text-left group hover:border-orange-500/40 transition-colors"
                style={{ borderColor: "var(--border)" }}
                data-testid={`swing-item-${it.id || idx}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                       style={{ background: p.tone.bg, color: p.tone.color, border: `1px solid ${p.tone.border}` }}>
                    {p.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-display font-black text-sm truncate">{it._assetLabel}</div>
                      <span className="pill !text-[10px] !py-0.5 !px-2"
                            style={{ background: p.tone.bg, color: p.tone.color, border: `1px solid ${p.tone.border}` }}>
                        {p.label}
                      </span>
                    </div>
                    <div className="text-[11px] font-mono-tight text-muted truncate">
                      {it._templateLabel}
                      {insp?.updated_at && <> · Updated {fmtRel(insp.updated_at)}</>}
                    </div>
                  </div>
                  <div className="text-right shrink-0 hidden sm:block">
                    <div className="font-display font-black text-lg tabular-nums" style={{ color: p.tone.color }}>{p.pct}%</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted group-hover:text-white transition-colors" />
                </div>

                {/* per-row progress */}
                <div className="h-1 rounded-full overflow-hidden mt-2"
                     style={{ background: "rgba(255,255,255,0.05)" }}>
                  <div className="h-full transition-all duration-500"
                       style={{ width: `${p.pct}%`, background: p.tone.color }} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- helpers ---------------- */
function extract(res) {
  if (res.status !== "fulfilled") return [];
  const d = res.value.data;
  return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
}

function isActiveSwing(s) {
  if (!s) return false;
  const status = String(s.status || s.state || "").toLowerCase();
  if (status === "closed" || status === "complete" || status === "completed" || status === "archived") return false;
  if (s.closed_at || s.closedAt) return false;
  const end = s.end_date || s.endDate;
  if (end) {
    const dt = new Date(end).getTime();
    // keep "active" until 24h past end_date so the tracker doesn't blink away
    if (!isNaN(dt) && dt < Date.now() - 86400000) return false;
  }
  return true;
}

function itemsFrom(swing, detail) {
  const raw =
    (detail && (detail.items || detail.inspections)) ||
    swing.items || swing.inspections || [];
  return raw.map((it) => {
    const asset = it.asset || {};
    const template = it.template || {};
    const insp = it.inspection || it; // some backends inline
    return {
      ...it,
      _insp: insp,
      _assetLabel:
        asset.name || asset.tag || it.asset_name || it.asset_tag ||
        (it.asset_id ? `Asset ${String(it.asset_id).slice(0, 8)}` : "Asset"),
      _templateLabel:
        template.name || template.title || it.template_name || it.template_title ||
        (it.template_id ? `Template ${String(it.template_id).slice(0, 8)}` : "Inspection"),
    };
  });
}

function progressFor(item, inspectionsById) {
  // Prefer an inspection linked by id
  const linked =
    (item._insp && item._insp.status && item._insp) ||
    inspectionsById.get(String(item.inspection_id || item._insp?.id || item._insp?._id));
  const insp = linked || item._insp || {};
  const status = String(insp.status || insp.stage || item.status || "").toLowerCase();

  // percent — prefer explicit fields, then derive from status
  let pct = null;
  if (typeof insp.progress === "number") pct = clamp(insp.progress);
  if (pct === null && typeof insp.completion === "number") pct = clamp(insp.completion);
  if (pct === null && Array.isArray(insp.items) && insp.items.length) {
    const done = insp.items.filter((x) => x.value != null && x.value !== "").length;
    pct = Math.round((done / insp.items.length) * 100);
  }

  if (status.includes("approved") || status.includes("complete") || status.includes("closed") || status.includes("signed")) {
    return { pct: 100, label: "Approved", tone: TONE.approved, icon: <CheckCircle2 className="w-4 h-4" /> };
  }
  if (status.includes("submit")) {
    return { pct: pct ?? 90, label: "Submitted", tone: TONE.submitted, icon: <PenTool className="w-4 h-4" /> };
  }
  if (status.includes("progress") || status.includes("draft") || status.includes("open")) {
    return { pct: pct ?? 40, label: "In progress", tone: TONE.progress, icon: <Loader2 className="w-4 h-4 animate-spin" /> };
  }
  if (status.includes("send") && status.includes("back")) {
    return { pct: pct ?? 20, label: "Sent back", tone: TONE.back, icon: <AlertTriangle className="w-4 h-4" /> };
  }
  return { pct: pct ?? 0, label: "Not started", tone: TONE.idle, icon: <Clock className="w-4 h-4" /> };
}

const TONE = {
  approved: { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
  submitted: { bg: "#1a2a55", border: "#3b60d1", color: "#8bb4ff" },
  progress:  { bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
  back:      { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" },
  idle:      { bg: "#141414", border: "#2a2a2a", color: "#a1a1aa" },
};

function clamp(n) { return Math.max(0, Math.min(100, Math.round(n <= 1 ? n * 100 : n))); }
function fmtDate(v) { if (!v) return null; try { return new Date(v).toLocaleDateString(); } catch { return String(v); } }
function fmtRange(a, b) {
  if (!a && !b) return null;
  const A = fmtDate(a), B = fmtDate(b);
  if (A && B && A !== B) return `${A} → ${B}`;
  return A || B;
}
function fmtRel(v) {
  try {
    const s = (Date.now() - new Date(v).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}